import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from '../../../utils/classNames';
import { ResolvedUserAvatar, UserAvatar } from '../UserAvatar';
import OnlineStatus from '../OnlineStatus';
import { useProfilePreview } from '../../../store/ProfilePreviewContext';

import './index.scss';

export default function UserCardAvatar({
  userId,
  username,
  alt = 'User avatar',
  badge,
  badgeTone = 'neutral',
  className,
  avatarClassName,
  size,
  radius,
  status,
  children,
  style,
  avatarUrl,
  fallbackSrc,
  imgProps,
  resolvedAvatar,
  enableProfilePreview = true,
  publicMedia = false,
  avatarId,
  defaultAvatarBackground = false,
  ...rest
}) {
  const wrapperStyle = { ...(style || {}) };
  const { openProfilePreview } = useProfilePreview();

  const {
    onClick,
    onKeyDown,
    role,
    tabIndex,
    ...restProps
  } = rest;

  if (size) {
    wrapperStyle['--avatar-size'] = size;
  }

  if (radius) {
    wrapperStyle['--avatar-radius'] = radius;
  }

  if (status && status.size) {
    wrapperStyle['--user-card-status-size'] = status.size;
    wrapperStyle['--online-status-size'] = status.size;
  }

  if (status && status.offset) {
    wrapperStyle['--user-card-status-offset'] = status.offset;
  }

  if (status && status.border) {
    wrapperStyle['--online-status-border'] = status.border;
  }

  const showProfilePreview = enableProfilePreview && Boolean(userId && openProfilePreview);

  const handleOpenProfile = useCallback(() => {
    if (showProfilePreview) {
      openProfilePreview(userId);
    }
  }, [openProfilePreview, showProfilePreview, userId]);

  const handleClick = useCallback((event) => {
    if (onClick) {
      onClick(event);
    }
    if (event?.defaultPrevented) {
      return;
    }
    if (!showProfilePreview) {
      return;
    }
    event.stopPropagation();
    handleOpenProfile();
  }, [onClick, showProfilePreview, handleOpenProfile]);

  const handleKeyDown = useCallback((event) => {
    if (onKeyDown) {
      onKeyDown(event);
    }
    if (event?.defaultPrevented) {
      return;
    }
    if (!showProfilePreview) {
      return;
    }
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      event.stopPropagation();
      handleOpenProfile();
    }
  }, [onKeyDown, showProfilePreview, handleOpenProfile]);

  const computedRole = role ?? (showProfilePreview ? 'button' : undefined);
  const computedTabIndex = showProfilePreview ? (tabIndex ?? 0) : tabIndex;
  const combinedClassName = classNames('user-card__avatar', className, {
    'user-card__avatar--interactive': showProfilePreview,
  });

  // Normalize status placement
  const statusPlacement = (status && status.placement) || 'br';

  return (
    <div
      className={combinedClassName}
      style={wrapperStyle}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role={computedRole}
      tabIndex={computedTabIndex}
      {...restProps}
    >
      {resolvedAvatar ? (
        <ResolvedUserAvatar
          alt={alt}
          avatarUrl={resolvedAvatar.avatarUrl}
          shouldUseSupportAvatar={Boolean(resolvedAvatar.shouldUseSupportAvatar)}
          className={classNames('user-card__avatar-media', avatarClassName)}
          radius={radius}
          fallbackSrc={fallbackSrc}
          imgProps={imgProps}
          id={avatarId}
          defaultAvatarBackground={
            defaultAvatarBackground
            && !resolvedAvatar.avatarUrl
            && !resolvedAvatar.shouldUseSupportAvatar
          }
        />
      ) : (
        <UserAvatar
          userId={userId}
          username={username}
          alt={alt}
          avatarUrl={avatarUrl}
          audience={publicMedia ? 'public' : 'viewer'}
          className={classNames('user-card__avatar-media', avatarClassName)}
          radius={radius}
          fallbackSrc={fallbackSrc}
          imgProps={imgProps}
          id={avatarId}
          defaultAvatarBackground={defaultAvatarBackground}
        />
      )}
      {status ? (
        <OnlineStatus
          online={Boolean(status.online)}
          lastSeen={status.lastSeen || null}
          className={classNames('user-card__status-dot', `user-card__status-dot--${statusPlacement}`)}
        />
      ) : null}
      {badge ? (
        <span className={classNames('user-card__badge', badgeTone && `user-card__badge--${badgeTone}`)}>
          {badge}
        </span>
      ) : null}
      {children}
    </div>
  );
}

UserCardAvatar.propTypes = {
  userId: PropTypes.string,
  username: PropTypes.string,
  alt: PropTypes.string,
  badge: PropTypes.node,
  badgeTone: PropTypes.string,
  className: PropTypes.string,
  avatarClassName: PropTypes.string,
  size: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  radius: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  status: PropTypes.shape({
    online: PropTypes.bool,
    lastSeen: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    placement: PropTypes.oneOf(['br', 'bl', 'tr', 'tl']),
    size: PropTypes.string,
    offset: PropTypes.string,
    border: PropTypes.string,
  }),
  children: PropTypes.node,
  style: PropTypes.object,
  avatarUrl: PropTypes.string,
  fallbackSrc: PropTypes.string,
  imgProps: PropTypes.object,
  resolvedAvatar: PropTypes.shape({
    avatarUrl: PropTypes.string,
    shouldUseSupportAvatar: PropTypes.bool,
  }),
  enableProfilePreview: PropTypes.bool,
  publicMedia: PropTypes.bool,
  defaultAvatarBackground: PropTypes.bool,
};
